
import React from 'react';
import { 
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TagManagementDialog } from './TagManagementDialog';
import { Badge } from '@/components/ui/badge';
import { useBoardContext } from '@/contexts/BoardContext';
import type { Tables } from '@/integrations/supabase/types';
import { 
  Trash2, 
  Edit, 
  Tag, 
  User, 
  Copy, 
  Calendar,
  CircleArrowUp,
  Move,
  Archive,
  ArchiveRestore
} from 'lucide-react';
import { useState } from 'react';

type CardType = Tables<'cards'> & { assigned_profile?: Tables<'profiles'> };

interface CardContextMenuProps {
  card: CardType;
  children: React.ReactNode;
  onEdit: () => void;
}

export const CardContextMenu = ({ card, children, onEdit }: CardContextMenuProps) => {
  const { 
    canEditCards, 
    deleteCard, 
    updateCard, 
    createCard, 
    columns, 
    tagDefinitions,
    profiles,
    archiveCard,
    unarchiveCard
  } = useBoardContext();
  
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  if (!canEditCards) {
    return <>{children}</>;
  }

  const handleDelete = () => {
    deleteCard(card.id);
    setShowDeleteDialog(false);
  };

  const handleDuplicate = () => {
    const newCard = {
      title: `${card.title} (Copy)`,
      description: card.description,
      column_id: card.column_id,
      position: 0, // Will be positioned at top
      tags: card.tags,
      category: card.category,
      checklists: card.checklists,
      created_by: card.created_by,
    };
    createCard(newCard);
  };

  const handleProgressChange = (newState: string) => {
    updateCard({
      cardId: card.id,
      updates: { progress_state: newState }
    });
  };

  const handleAssignToMe = () => {
    updateCard({
      cardId: card.id,
      updates: { assigned_to: card.created_by }
    });
  };

  const handleUnassign = () => {
    updateCard({
      cardId: card.id,
      updates: { assigned_to: null }
    });
  };

  const handleMoveToColumn = (columnId: string) => {
    updateCard({
      cardId: card.id,
      updates: { column_id: columnId, position: 0 }
    });
  };

  const handleAddTag = (tagId: string) => {
    const currentTags = card.tags || [];
    if (!currentTags.includes(tagId)) {
      updateCard({
        cardId: card.id,
        updates: { tags: [...currentTags, tagId] }
      });
    }
  };

  const handleRemoveTag = (tagId: string) => {
    const currentTags = card.tags || [];
    updateCard({
      cardId: card.id,
      updates: { tags: currentTags.filter(t => t !== tagId) }
    });
  };

  const handleSetDueToday = () => {
    const today = new Date().toISOString().split('T')[0];
    updateCard({
      cardId: card.id,
      updates: { end_date: today }
    });
  };

  const handleSetDueTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    updateCard({
      cardId: card.id,
      updates: { end_date: tomorrowStr }
    });
  };

  const handleClearDueDate = () => {
    updateCard({
      cardId: card.id,
      updates: { end_date: null }
    });
  };

  const handleArchive = () => {
    archiveCard(card.id);
  };

  const handleUnarchive = () => {
    unarchiveCard(card.id);
  };

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          {children}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-56">
          <ContextMenuItem onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Card
          </ContextMenuItem>
          
          <ContextMenuItem onClick={handleDuplicate}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicate
          </ContextMenuItem>

          <ContextMenuSeparator />

          {/* Archive/Unarchive Actions */}
          {card.archived ? (
            <ContextMenuItem onClick={handleUnarchive}>
              <ArchiveRestore className="h-4 w-4 mr-2" />
              Unarchive Card
            </ContextMenuItem>
          ) : (
            <ContextMenuItem onClick={handleArchive}>
              <Archive className="h-4 w-4 mr-2" />
              Archive Card
            </ContextMenuItem>
          )}

          <ContextMenuSeparator />

          {/* Progress State Actions */}
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <CircleArrowUp className="h-4 w-4 mr-2" />
              Progress
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              <ContextMenuItem onClick={() => handleProgressChange('not_started')}>
                Not Started
              </ContextMenuItem>
              <ContextMenuItem onClick={() => handleProgressChange('in_progress')}>
                In Progress
              </ContextMenuItem>
              <ContextMenuItem onClick={() => handleProgressChange('completed')}>
                Completed
              </ContextMenuItem>
            </ContextMenuSubContent>
          </ContextMenuSub>

          {/* Assignment Actions */}
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <User className="h-4 w-4 mr-2" />
              Assign
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              <ContextMenuItem onClick={handleAssignToMe}>
                Assign to Me
              </ContextMenuItem>
              {card.assigned_to && (
                <ContextMenuItem onClick={handleUnassign}>
                  Unassign
                </ContextMenuItem>
              )}
            </ContextMenuSubContent>
          </ContextMenuSub>

          {/* Tag Management */}
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <Tag className="h-4 w-4 mr-2" />
              Tags
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {tagDefinitions?.map((tag) => {
                const isApplied = card.tags?.includes(tag.id);
                return (
                  <ContextMenuItem
                    key={tag.id}
                    onClick={() => isApplied ? handleRemoveTag(tag.id) : handleAddTag(tag.id)}
                  >
                    <Badge
                      style={{ backgroundColor: tag.color, color: 'white' }}
                      className="mr-2 text-xs"
                    >
                      {tag.name}
                    </Badge>
                    {isApplied ? 'Remove' : 'Add'}
                  </ContextMenuItem>
                );
              })}
              <ContextMenuSeparator />
              <TagManagementDialog
                onTagSelected={handleAddTag}
                selectionMode={true}
              >
                <ContextMenuItem onSelect={(e) => e.preventDefault()}>
                  <Tag className="h-4 w-4 mr-2" />
                  Manage Tags...
                </ContextMenuItem>
              </TagManagementDialog>
            </ContextMenuSubContent>
          </ContextMenuSub>

          {/* Move to Column */}
          {columns && columns.length > 1 && (
            <ContextMenuSub>
              <ContextMenuSubTrigger>
                <Move className="h-4 w-4 mr-2" />
                Move to
              </ContextMenuSubTrigger>
              <ContextMenuSubContent>
                {columns
                  .filter(col => col.id !== card.column_id)
                  .map((column) => (
                    <ContextMenuItem
                      key={column.id}
                      onClick={() => handleMoveToColumn(column.id)}
                    >
                      {column.title}
                    </ContextMenuItem>
                  ))}
              </ContextMenuSubContent>
            </ContextMenuSub>
          )}

          {/* Date Actions */}
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <Calendar className="h-4 w-4 mr-2" />
              Due Date
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              <ContextMenuItem onClick={handleSetDueToday}>
                Due Today
              </ContextMenuItem>
              <ContextMenuItem onClick={handleSetDueTomorrow}>
                Due Tomorrow
              </ContextMenuItem>
              {card.end_date && (
                <ContextMenuItem onClick={handleClearDueDate}>
                  Clear Due Date
                </ContextMenuItem>
              )}
            </ContextMenuSubContent>
          </ContextMenuSub>

          <ContextMenuSeparator />

          <ContextMenuItem 
            onClick={() => setShowDeleteDialog(true)}
            className="text-red-600 focus:text-red-600 dark:text-red-400"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Card
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Card</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{card.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
